import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, qs } from '../lib/api';
import { addDays, date, eur, pct, today } from '../lib/format';
import { useSession } from '../lib/session';
import { Badge, Empty, ErrorBox, Kpi, Loading, PageHead, useLoad } from '../components/ui';

const REASON_LABEL: Record<string, string> = {
  AMOUNT_ATYPICAL: 'Importe atípico', BASE_MISMATCH: 'Base no cuadra', CURRENCY: 'Otra divisa', CUSTOMER_MISMATCH: 'Emitida a otro NIF',
  DATE_FUTURE: 'Fecha futura', DATE_OLD: 'Fecha antigua', DATE_PRESCRIBED: 'IVA posiblemente prescrito', DN_MISSING: 'Sin albarán', DN_PRICE: 'Precio ≠ albarán', DN_QTY: 'Cantidad ≠ albarán',
  DOC_DELIVERY_NOTE: 'Es un albarán', DOC_NOT_INVOICE: 'No es una factura', DOC_PROFORMA: 'Es una proforma', DUE_BEFORE_ISSUE: 'Vence antes de emitirse',
  DUP_APPROX: 'Posible duplicado', DUP_FILE: 'Archivo repetido', DUP_NUMBER: 'Duplicado',
  IBAN_CHANGED: 'Cambio de IBAN', IBAN_INVALID: 'IBAN no válido', IBAN_NON_SEPA: 'IBAN fuera de SEPA', IBAN_UNVERIFIED: 'IBAN sin verificar',
  INTRA_EU: 'Intracomunitaria', INTRA_WITH_VAT: 'Intracomunitaria con IVA', ISP: 'Inversión del sujeto pasivo', ISP_WITH_VAT: 'ISP con IVA repercutido',
  LINES_VS_BASE: 'Líneas ≠ base', LINE_MISMATCH: 'Línea no cuadra', LOW_CONFIDENCE_SOURCE: 'Lectura sin IA', NEGATIVE_NOT_RECTIFYING: 'Negativa sin ser rectificativa',
  NIF_EU: 'NIF europeo', NIF_FOREIGN: 'NIF extranjero', NIF_INVALID: 'NIF no válido', PO_AMOUNT: 'Importe ≠ pedido', PO_NOT_FOUND: 'Pedido no encontrado',
  PROMPT_INJECTION: 'Texto sospechoso', RATE_HISTORICAL: 'Tipo no vigente', RATE_INVALID: 'Tipo de IVA no válido', RECTIFYING_NO_REF: 'Rectificativa sin referencia',
  REGIME_MISMATCH: 'Impuesto de otro territorio', REQ_CUSTOMER_NIF: 'Falta NIF destinatario', REQ_DATE: 'Falta fecha', REQ_NUMBER: 'Falta número',
  REQ_SUPPLIER_NAME: 'Falta nombre del proveedor', REQ_SUPPLIER_NIF: 'Falta NIF del proveedor', REQ_TAX_BREAKDOWN: 'Falta desglose de IVA', REQ_TOTAL: 'Falta total',
  SELF_INVOICE: 'Emitida por la propia empresa', SIMPLIFIED_OVER_LIMIT: 'Simplificada por encima del límite', SIMPLIFIED_VAT: 'Simplificada sin IVA deducible',
  SURCHARGE_AMOUNT: 'Recargo no cuadra', SURCHARGE_RATE: 'Tipo de recargo', TAX_AMOUNT_MISMATCH: 'Cuota no cuadra', TERM_OVER_60: 'Plazo > 60 días',
  TOTAL_MISMATCH: 'Total no cuadra', TOTAL_UNVERIFIABLE: 'Total no verificable', UNANCHORED: 'Dato no localizado', VENDOR_BLOCKED: 'Proveedor bloqueado',
  VENDOR_LOOKALIKE: 'Proveedor parecido a otro', VENDOR_NEW: 'Proveedor nuevo', WITHHOLDING_AMOUNT: 'Retención no cuadra', WITHHOLDING_MISSING: 'Posible retención',
  WITHHOLDING_RATE: 'Tipo de retención', ZERO_WITHOUT_MENTION: 'Exenta sin mención legal',
};

export function Dashboard() {
  const s = useSession();
  const [range, setRange] = useState(90);
  const from = addDays(today(), -range);
  const { data: m, error, loading } = useLoad(() => api.get(`/metrics${qs({ companyId: s.companyId, from, to: today() })}`), [s.companyId, range]);
  const cal = useLoad(() => api.get(`/payments/calendar${qs({ companyId: s.companyId, days: 45 })}`), [s.companyId]);
  if (loading && !m) return <Loading />;
  return (
    <>
      <PageHead title="Panel" subtitle={s.company ? s.company.name : 'Todas las empresas'} actions={
        <select value={range} onChange={(e) => setRange(Number(e.target.value))} style={{ width: 180 }}>
          <option value={30}>Últimos 30 días</option><option value={90}>Últimos 90 días</option><option value={365}>Último año</option>
        </select>} />
      <ErrorBox error={error} />
      {m && (
        <div className="stack">
          <div className="grid4">
            <Kpi value={m.received} label="Facturas recibidas" />
            <Kpi value={pct(m.touchlessRate)} label="Sin intervención humana" hint="Facturas aprobadas sin editar ningún dato" />
            <Kpi value={m.estimatedMinutesSaved ? `${Math.round(m.estimatedMinutesSaved / 60)} h` : '—'} label={`Tiempo ahorrado (≈ ${eur(m.estimatedSavingsEur)})`} hint="Estimación frente a la línea base configurada en Ajustes" />
            <Kpi value={m.costPerInvoiceEur !== null ? eur(m.costPerInvoiceEur) : '—'} label="Coste de lectura por factura" />
          </div>
          <div className="grid4">
            <Kpi value={pct(m.exceptionRate)} label="Facturas con incidencias" />
            <Kpi value={m.medianHoursToApprove !== null ? `${m.medianHoursToApprove} h` : '—'} label="Mediana hasta aprobación" />
            <Kpi value={m.duplicatesBlocked} label="Duplicados frenados" />
            <Kpi value={m.ibanAlerts} label="Alertas de cambio de IBAN" />
          </div>
          {(m.einvoice.pending > 0 || m.einvoice.overdue > 0) && (
            <div className={`alert ${m.einvoice.overdue ? 'error' : 'warn'}`}>
              Factura electrónica: {m.einvoice.pending} estado(s) por comunicar{m.einvoice.overdue ? `, ${m.einvoice.overdue} fuera de plazo` : ''}. <Link to="/factura-electronica">Ver</Link>
            </div>
          )}
          <div className="grid2">
            <div className="card">
              <h2>Precisión de la lectura por dato</h2>
              <p className="hint">Porcentaje de facturas aprobadas en las que nadie tuvo que corregir el dato.</p>
              <div className="stack">
                {Object.entries(m.fieldAccuracy as Record<string, number>).map(([k, v]) => (
                  <div key={k}>
                    <div className="row between"><span>{({ invoice_number: 'Número', issue_date: 'Fecha', 'supplier.nif': 'NIF proveedor', 'supplier.name': 'Proveedor', 'totals.base': 'Base', 'totals.tax': 'Cuota', 'totals.total': 'Total', iban: 'IBAN', account: 'Cuenta contable' } as any)[k] ?? k}</span><span className="num">{pct(v)}</span></div>
                    <div className="bar"><div style={{ width: `${v}%`, background: v < 90 ? 'var(--warn)' : undefined }} /></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card">
              <h2>Motivos de revisión más frecuentes</h2>
              {m.topReasons.length === 0 ? <Empty>Sin incidencias en el periodo.</Empty> : (
                <table><tbody>{m.topReasons.map((r: any) => <tr key={r.code}><td>{REASON_LABEL[r.code] ?? r.code}</td><td className="num">{r.n}</td></tr>)}</tbody></table>
              )}
              <h2 style={{ marginTop: 16 }}>Principales proveedores</h2>
              <table><tbody>{m.topVendors.map((v: any) => <tr key={v.name}><td>{v.name}</td><td className="num">{v.n}</td><td className="num">{eur(v.total)}</td></tr>)}</tbody></table>
            </div>
          </div>
          <div className="card">
            <h2>Vencimientos próximos (45 días) <Link to={s.hasModule('pagos') ? '/pagos' : '/'} className="hint">Ver</Link></h2>
            {(cal.data ?? []).length === 0 ? <Empty>No hay vencimientos pendientes.</Empty> : (
              <div className="table-wrap"><table>
                <thead><tr><th>Día</th><th className="num">Facturas</th><th className="num">Importe</th><th /></tr></thead>
                <tbody>{cal.data.map((d: any) => <tr key={d.day}><td>{date(d.day)}</td><td className="num">{d.n}</td><td className="num">{eur(d.total)}</td><td>{d.overdue > 0 && <Badge tone="error">Vencida</Badge>}</td></tr>)}</tbody>
              </table></div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function Advisor() {
  const s = useSession();
  const nav = useNavigate();
  const { data, error, loading } = useLoad(() => api.get('/advisor/overview'), []);
  if (loading) return <Loading />;
  return (
    <>
      <PageHead title="Empresas" subtitle="Estado de todas las empresas que gestionas." />
      <ErrorBox error={error} />
      <div className="table-wrap">
        <table>
          <thead><tr><th>Empresa</th><th className="num">Leyendo</th><th className="num">En revisión</th><th className="num">En aprobación</th><th className="num">Por contabilizar</th><th className="num">Factura electrónica</th><th className="num">IBAN por verificar</th><th className="num">Últimos 30 días</th><th>Última recibida</th></tr></thead>
          <tbody>
            {(data ?? []).map((c: any) => (
              <tr key={c.id} className="clickable" onClick={() => { s.setCompanyId(c.id); nav('/'); }}>
                <td><strong>{c.name}</strong><div className="muted mono">{c.nif}</div></td>
                <td className="num">{c.processing || ''}</td>
                <td className="num">{c.review ? <Badge tone="warn">{c.review}</Badge> : ''}</td>
                <td className="num">{c.pending_approval || ''}</td>
                <td className="num">{c.to_export ? <Badge tone="ok">{c.to_export}</Badge> : ''}</td>
                <td className="num">{c.einvoice_overdue ? <Badge tone="error">{c.einvoice_overdue} fuera de plazo</Badge> : c.einvoice_pending || ''}</td>
                <td className="num">{c.iban_pending ? <Badge tone="warn">{c.iban_pending}</Badge> : ''}</td>
                <td className="num">{c.last30}</td>
                <td>{c.last_received ? date(c.last_received) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
